import { all, call, put, takeLatest } from 'redux-saga/effects';
import fileDownload from 'js-file-download';
import { fetchClient } from '../utilities/fetchClient';
import ActionTypes from '../actions/actionTypes';
import {
  detailsDownloadSuccess,
  detailsDownloadFailure,
  getAccountsSuccess,
  getAccountsFailure,
  getTransferCardTargetAccountsSuccess,
  getTransferCardTargetAccountsFailure,
  getPaymentTypeSuccess,
  getPaymentTypeFailure,
  makePaymentSuccess,
  makePaymentFailure,
  getGrandparentAccountNameSuccess,
  getGrandparentAccountNameFailure,
  getContractBalancesSuccess,
  getContractBalancesFailure,
  getAcquiringContractAddressSuccess,
  getAcquiringContractAddressFailure,
  getAccountsForUserSuccess,
  getAccountsForUserFailure,
} from '../actions/accountActions';
import { accountsDownloadFileName, invoiceStatementFileName } from '../constants';
import { isMobile } from '../utilities/usersDevice';
import Env from '../Env';
import dayjs from 'dayjs';
import { addToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';
import { setAssignedGrandparentAccount } from 'actions/userActions';
import SURVEYS from 'constants/surveys';

function* getAccounts(action) {
  try {
    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };
    const { accountNumber, ...queryParams } = action.payload;

    const response = yield call(fetchClient, {
      url: `accounts-api/v1/accounts/contract/list/${accountNumber}`,
      method: 'GET',
      headers: header,
      queryParams: queryParams,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put(
      getAccountsSuccess(
        action.type === ActionTypes.ACCOUNTS.REQUEST ? ActionTypes.ACCOUNTS.SUCCESS : ActionTypes.ACCOUNTS.ALL.SUCCESS,
        response
      )
    );
  } catch {
    yield put(
      getAccountsFailure(
        action.type === ActionTypes.ACCOUNTS.REQUEST ? ActionTypes.ACCOUNTS.FAILURE : ActionTypes.ACCOUNTS.ALL.FAILURE
      )
    );
  }
}

function* getTransferCardTargetAccounts(action) {
  try {
    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    const response = yield call(fetchClient, {
      url:
        `accounts-api/v1/accounts/contract/list/${action.payload.clientAccountId}?contractSearchMethod=${action.payload.contractSearchMethod}` +
        `&pageSize=${action.payload.pageSize}&outObjectPage=${action.payload.outObjectPage}` +
        `&sortOrder=${action.payload.sortOrder}&sortBy=${action.payload.sortBy}` +
        `&searchTerm=${action.payload.searchTerm}&grandParentAccountNumber=${action.payload.grandParentAccountNumber}`,
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put(getTransferCardTargetAccountsSuccess(response));
  } catch {
    yield put(getTransferCardTargetAccountsFailure());
  }
}

function* getAccountsFile(action) {
  try {
    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };
    yield put(addToastAlert(Alert.DOWNLOAD_ACCOUNTS_LOADING));
    const response = yield call(fetchClient, {
      method: 'GET',
      url: `accounts-api/v1/accounts/contract/list/${action.payload.contractIdentifier}/download`,
      queryParams: {
        contractSearchMethod: 'CONTRACT_NUMBER',
        grandParentAccountNumber: action.payload.grandParentAccountNumber,
        searchTerm: action.payload.searchTerm,
      },
      headers: header,
      apiServer: Env.getApimBaseUrl(),
      responseType: 'blob',
    });

    if (isMobile()) {
      var reader = new FileReader();
      var blobData = new Blob([response], { type: 'application/vnd.ms-excel' });
      reader.onload = function () {
        window.location.href = reader.result;
      };
      reader.readAsDataURL(blobData);
    } else {
      yield call(fileDownload, response, accountsDownloadFileName);
    }

    yield put(detailsDownloadSuccess());
    yield put(addToastAlert(Alert.DOWNLOAD_ACCOUNTS_SUCCESS, 'success'));
  } catch {
    yield put(detailsDownloadFailure());
    yield put(addToastAlert(Alert.DOWNLOAD_ACCOUNTS_ERROR, 'error'));
  }
}

function* getAddressesByContract(action) {
  try {
    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };
    const response = yield call(fetchClient, {
      url: `accounts-api/v1/accounts/contract/address/${action.payload.contractIdentifier}`,
      method: 'GET',
      headers: header,
      queryParams: {
        contractSearchMethod: 'CONTRACT_NUMBER',
        grandParentAccountNumber: action.payload.grandParentAccountNumber,
      },
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.ADDRESSES.SUCCESS,
      payload: {
        addressesDetails: response.outObject,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.ADDRESSES.FAILURE, payload: e });
  }
}

function* updateAddressesByContract(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    yield put(addToastAlert(Alert.UPDATE_ADDRESS_LOADING));
    yield call(fetchClient, {
      url: 'accounts-api/v1/accounts/contract/address/update',
      method: 'POST',
      body: action.payload,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.ADDRESSES.UPDATE.SUCCESS,
      payload: {
        addressesDetails: action.payload,
      },
    });
    yield put(addToastAlert(Alert.UPDATE_ADDRESS_SUCCESS, 'success'));
    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.UPDATING_ACCOUNT_ADDRESSES) {
      window._zf('startForm', { 'widgetId': SURVEYS.UPDATING_ACCOUNT_ADDRESSES, 'throttle': 'session' });
    }
  } catch (e) {
    yield put({ type: ActionTypes.ADDRESSES.UPDATE.FAILURE, payload: e });
    yield put(addToastAlert(Alert.UPDATE_ADDRESS_ERROR, 'error'));
  }
}

function* getInvoiceFileDownload(action) {
  const { grandParentAccountNumber, contractIdentifier, fileType } = action.payload;
  let downloadedFileName = `${grandParentAccountNumber}_${contractIdentifier}_${invoiceStatementFileName}_${dayjs().format('MM-DD-YYYY-hh:mm:ss')}.${fileType}`;

  try {
    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
      'Content-disposition': 'attachment',
    };
    yield put(addToastAlert(Alert.DOWNLOAD_INVOICES_LOADING));
    const response = yield call(fetchClient, {
      url: `reports-api/v1/reports/invoicing/${action.payload.grandParentAccountNumber}/${action.payload.contractIdentifier}/downloadDocument/${action.payload.documentId}`,
      method: 'GET',
      body: action.payload,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
      responseType: 'blob',
    });
    yield call(fileDownload, response, downloadedFileName);

    yield put({
      type: ActionTypes.ACCOUNTS.INVOICES.DOWNLOAD_SINGLE_FILE.SUCCESS,
    });
    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.DOWNLOADING_INVOICE_STATEMENTS) {
      window._zf('startForm', { 'widgetId': SURVEYS.DOWNLOADING_INVOICE_STATEMENTS, 'throttle': 'session' });
    }
    yield put(addToastAlert(Alert.DOWNLOAD_INVOICES_SUCCESS, 'success'));
  } catch (e) {
    yield put({ type: ActionTypes.ACCOUNTS.INVOICES.DOWNLOAD_SINGLE_FILE.FAILURE, payload: e });
    yield put(addToastAlert(Alert.DOWNLOAD_INVOICES_ERROR, 'error'));
  }
}

function* getPaymentType(action) {
  const { grandParentAccountNumber, accountNumber } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url: `accounts-api/v1/accounts/contract/${accountNumber}/paymenttype`,
      method: 'GET',
      headers: header,
      queryParams: {
        grandParentAccountNumber,
      },
      apiServer: Env.getApimBaseUrl(),
    });
    yield put(getPaymentTypeSuccess(response));
  } catch {
    yield put(getPaymentTypeFailure());
  }
}

function* makePayment(action) {
  const { accountNumber } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    yield put(addToastAlert(Alert.MAKE_PAYMENT_LOADING));
    const response = yield call(fetchClient, {
      url: `accounts-api/v1/accounts/contract/${accountNumber}/payment/adjustment`,
      method: 'POST',
      body: action.payload,
      apiServer: Env.getApimBaseUrl(),
      headers: header,
    });
    yield put(makePaymentSuccess({ ...response, accountNumber }));
    yield put(addToastAlert(Alert.MAKE_PAYMENT_SUCCESS, 'success'));
    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.RUNNING_DIRECT_DEBIT_PAYMENT) {
      window._zf('startForm', { 'widgetId': SURVEYS.RUNNING_DIRECT_DEBIT_PAYMENT, 'throttle': 'session' });
    }
  } catch {
    yield put(makePaymentFailure());
    yield put(addToastAlert(Alert.MAKE_PAYMENT_ERROR, 'error'));
  }
}

function* getGrandparentAccountName(action) {
  const { accountNumber } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url: `accounts-api/v1/accounts/contract/${accountNumber}/grandparentname`,
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
    });
    yield put(getGrandparentAccountNameSuccess(response));
    const { contractNumber: grandParentAccountNumber, contractName: name } = response;
    yield put(
      setAssignedGrandparentAccount({ grandParentAccountNumber, name, accountNumber: grandParentAccountNumber })
    );
  } catch {
    yield put(getGrandparentAccountNameFailure());
  }
}

function* getContractBalances(action) {
  const { accountNumber, assignedGrandfatherClientNumber } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url: `accounts-api/v1/accounts/contract/balance/${accountNumber}?grandParentAccountNumber=${assignedGrandfatherClientNumber}&balanceList=PURCHASE,FEE,CREDIT,ADJUSTMENT,PAYMENT`,
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
    });
    yield put(getContractBalancesSuccess(response));
  } catch {
    yield put(getContractBalancesFailure());
  }
}

function* getAcquiringContractAddress(action) {
  const { contractIdentifier } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url: `accounts-api/v1/accounts/contract/acquire/address/${contractIdentifier}?ContractSearchMethod=CONTRACT_NUMBER`,
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
    });
    yield put(getAcquiringContractAddressSuccess(response));
  } catch {
    yield put(getAcquiringContractAddressFailure());
  }
}

function* getAccountsForUser(action) {
  try {
    const { userId, accountNumber, ...queryParams } = action.payload;

    const headerPayload = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    const response = yield call(fetchClient, {
      url: `accounts-api/v1/${userId}/contracts/${accountNumber}`,
      method: 'GET',
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl(),
      queryParams: {
        ...queryParams,
      },
    });
    yield put(getAccountsForUserSuccess(response));
  } catch (e) {
    yield put(getAccountsForUserFailure());
  }
}

export default function* accountsSaga() {
  yield all([
    takeLatest(ActionTypes.ACCOUNTS.REQUEST, getAccounts),
    takeLatest(ActionTypes.ACCOUNTS.ALL.REQUEST, getAccounts),
    takeLatest(ActionTypes.ACCOUNTS.TRANSFER_CARD_TARGET_ACCOUNTS.REQUEST, getTransferCardTargetAccounts),
    takeLatest(ActionTypes.ACCOUNTS.DOWNLOAD.REQUEST, getAccountsFile),
    takeLatest(ActionTypes.ADDRESSES.REQUEST, getAddressesByContract),
    takeLatest(ActionTypes.ADDRESSES.UPDATE.REQUEST, updateAddressesByContract),
    takeLatest(ActionTypes.ACCOUNTS.INVOICES.DOWNLOAD_SINGLE_FILE.REQUEST, getInvoiceFileDownload),
    takeLatest(ActionTypes.ACCOUNTS.PAYMENT_TYPE.REQUEST, getPaymentType),
    takeLatest(ActionTypes.ACCOUNTS.PAYMENT.REQUEST, makePayment),
    takeLatest(ActionTypes.ACCOUNTS.GRANDPARENT_ACCOUNT_NAME.REQUEST, getGrandparentAccountName),
    takeLatest(ActionTypes.ACCOUNTS.CONTRACT_BALANCES.REQUEST, getContractBalances),
    takeLatest(ActionTypes.ACCOUNTS.ACQUIRING_CONTRACT_ADDRESS.REQUEST, getAcquiringContractAddress),
    takeLatest(ActionTypes.ACCOUNTS.GET_ACCOUNTS_FOR_USER.REQUEST, getAccountsForUser),
  ]);
}
